import { Skeleton, useMediaQuery } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AICoachService } from "../api/AICoachService";
import { TrainingService } from "../api/TrainingService";
import { TrainingChatBot } from "../components/TrainingChatBot/TrainingChatBot";
import { Button } from "../components/ui/Button/Button";
import { Logo } from "../components/ui/Logo";
import { Typography } from "../components/ui/Typography/Typography";
import { IconTypes } from "../components/ui/icons";
import { Icon } from "../components/ui/icons/Icon";
import useTranslation from "../hooks/useTranslation";
import ChecklistIllustration from "../styles/illustrations/ChecklistIllustration.png";
import {
  ChatHistoryEntry,
  ChatHistoryResponse,
  ChatMessage,
} from "../types/aiCoach";
import { generateOptimisticId } from "../utils/chatbot.utils";
import { extractServerMessage } from "../utils/error.utils";
import "./TrainingCompletionPage.scss";

const CHAT_REGION_ID = "training-chat-region";

// Server doesn't expose stable per-message ids, so derive one from role + content
// + position. Keys stay stable across re-renders of the same conversation,
// but a content change at a given position correctly invalidates the bubble.
const historyToMessages = (
  history: ChatHistoryEntry[],
  sessionId?: string
): ChatMessage[] =>
  history.map((entry, index) => ({
    id: `${sessionId ?? "anon"}-${index}-${entry.role}`,
    role: entry.role,
    message: entry.message,
  }));

export const TrainingCompletionPage = () => {
  const { t } = useTranslation("trainingCompletionPage");
  const { search } = useLocation();
  const trainingService = useMemo(() => new TrainingService(), []);
  const aiCoachService = useMemo(() => new AICoachService(), []);
  const queryClient = useQueryClient();

  const params = new URLSearchParams(search);
  const learners = params.get("learners");
  const touchpointRegistrations = params.get("touchpoint-registrations");

  const { data: completionData, isLoading: recordingLoading } = useQuery(
    ["training-completion", learners, touchpointRegistrations],
    () =>
      trainingService.recordTrainingCompletion(
        learners!,
        touchpointRegistrations!
      ),
    {
      enabled: !!(learners && touchpointRegistrations),
      retry: false,
    }
  );

  const touchpointTitle = completionData?.touchpoint_info.title;
  const trainingModuleTitle =
    completionData?.touchpoint_info.training_module.title;
  const touchpointId = completionData?.touchpoint_info.reference_id;
  const isDesktop = useMediaQuery("(min-width:900px)");
  const [mobileView, setMobileView] = useState<"chat" | "celebration">("chat");

  // Chat state — controlled from this page so we can persist messages across
  // mobileView toggles and thread session_id through mutations.
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isQuotaExhausted, setIsQuotaExhausted] = useState(false);
  const hydratedRef = useRef(false);
  const initErrorAppendedRef = useRef(false);

  const chatQueryKey = useMemo(
    () => ["training-chat", learners, touchpointId] as const,
    [learners, touchpointId]
  );

  const {
    data: chatHistory,
    error: chatInitError,
    isError: chatInitFailed,
    isInitialLoading: chatInitLoading,
  } = useQuery<ChatHistoryResponse, AxiosError>(
    chatQueryKey,
    () => aiCoachService.getChat(learners!, touchpointId!),
    {
      enabled: !!(learners && touchpointId),
      retry: false,
      // Mutation owns the conversation after first hydration; refetching would
      // clobber any in-flight optimistic bubble.
      staleTime: Infinity,
      refetchOnMount: false,
    }
  );

  // The GET endpoint serves three purposes — availability check, quota check,
  // and history fetch — so its status code drives the page-level chat state:
  //   404 → no chatbot for this learner+touchpoint, hide the panel entirely
  //   403 → chatbot creation quota reached, show "limit reached" + lock composer
  //   other failures → generic error bubble + locked composer (transient)
  // The chat query only fires once we have a touchpointId from the completion
  // response, so if recording finished without one (failed or missing id) the
  // chat is also unreachable — hide the panel rather than rendering a dead one.
  const chatInitStatus = chatInitError?.response?.status;
  const isChatUnavailable =
    chatInitStatus === 404 || (!recordingLoading && !touchpointId);
  const isChatQuotaReached = chatInitStatus === 403;

  // Defer rendering the chat panel until both queries have settled. Without
  // this, the panel renders during the recording + chat-init fetches and then
  // disappears for touchpoints where the chat ends up unavailable, producing
  // a visible flicker.
  const isChatPending = recordingLoading || (!!touchpointId && chatInitLoading);

  const showCelebration =
    isDesktop ||
    mobileView === "celebration" ||
    isChatUnavailable ||
    isChatPending;
  const showChat =
    !isChatUnavailable &&
    !isChatPending &&
    (isDesktop || mobileView === "chat");

  // The hydration and init-error guards are per-query, so when learner+touchpoint
  // change (e.g. completion data arrives or query params change) we need to allow
  // those effects to fire again for the new chat thread.
  useEffect(() => {
    hydratedRef.current = false;
    initErrorAppendedRef.current = false;
    setIsQuotaExhausted(false);
    setSessionId(undefined);
    setChatMessages([]);
  }, [learners, touchpointId]);

  useEffect(() => {
    if (!chatHistory || hydratedRef.current) return;
    hydratedRef.current = true;
    // hydratedRef gates this to a single run per chat identity, so the
    // setState calls below cannot cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessionId(chatHistory.session_id);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChatMessages(
      historyToMessages(chatHistory.history, chatHistory.session_id)
    );
  }, [chatHistory]);

  // Surface a chat-init failure as an assistant bubble. Without this the user
  // sees an empty chat indistinguishable from a fresh session, and any send
  // would post with sessionId=undefined — orphaning whatever thread the server
  // already has for this learner+touchpoint. The composer is also locked below
  // until the user refreshes (or permanently, for the 403 quota case). Skipped
  // for 404, since in that case the chat panel is hidden entirely.
  useEffect(() => {
    if (!chatInitError || isChatUnavailable || initErrorAppendedRef.current)
      return;
    initErrorAppendedRef.current = true;
    const message = isChatQuotaReached
      ? String(t("aiCoach.errorQuotaReached"))
      : extractServerMessage(chatInitError) ??
        String(t("aiCoach.errorChatLoad"));
    // initErrorAppendedRef gates this to a single append per error, so the
    // setState calls below cannot cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChatMessages((prev) => [
      ...prev,
      { id: generateOptimisticId(), role: "assistant", message },
    ]);
    if (isChatQuotaReached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsQuotaExhausted(true);
    }
  }, [chatInitError, isChatUnavailable, isChatQuotaReached, t]);

  const sendMutation = useMutation({
    mutationFn: (question: string) =>
      aiCoachService.postQuestion(learners!, touchpointId!, {
        question,
        session_id: sessionId,
      }),
    onSuccess: (data) => {
      setSessionId(data.session_id);
      setChatMessages(historyToMessages(data.history, data.session_id));
      // Keep the cached chat history in sync — the query has
      // staleTime: Infinity, so a remount would otherwise rehydrate from
      // pre-send state.
      queryClient.setQueryData(chatQueryKey, {
        session_id: data.session_id,
        history: data.history,
      });
    },
    onError: (error) => {
      // Keep the optimistic user message and append an assistant bubble — chat
      // UX makes more sense than silently dropping the turn. A 403 from this
      // endpoint always means the per-learner query quota is exhausted, so we
      // show the shared "limit reached" copy and lock further sends. Any other
      // failure surfaces the server's message (or a generic fallback) without
      // locking the composer, so the user can retry.
      const status =
        error instanceof AxiosError ? error.response?.status : undefined;
      const isQuotaError = status === 403;
      const message = isQuotaError
        ? String(t("aiCoach.errorQuotaReached"))
        : extractServerMessage(error) ?? String(t("aiCoach.errorGeneric"));
      setChatMessages((prev) => [
        ...prev,
        {
          id: generateOptimisticId(),
          role: "assistant",
          message,
        },
      ]);
      if (isQuotaError) {
        setIsQuotaExhausted(true);
      }
    },
  });

  const handleSend = (question: string) => {
    if (
      !learners ||
      !touchpointId ||
      isQuotaExhausted ||
      chatInitFailed ||
      chatInitLoading
    ) {
      return;
    }
    setChatMessages((prev) => [
      ...prev,
      { id: generateOptimisticId(), role: "user", message: question },
    ]);
    sendMutation.mutate(question);
  };

  // Focus management for the mobile celebration <-> chat toggle. Without this,
  // focus drops to <body> when the active region unmounts.
  const openChatButtonRef = useRef<HTMLButtonElement | null>(null);
  const chatRegionRef = useRef<HTMLDivElement | null>(null);
  const prevMobileViewRef = useRef(mobileView);
  useEffect(() => {
    if (isDesktop) return;
    const prev = prevMobileViewRef.current;
    if (prev !== mobileView) {
      if (mobileView === "chat") {
        chatRegionRef.current?.focus();
      } else {
        openChatButtonRef.current?.focus();
      }
    }
    prevMobileViewRef.current = mobileView;
  }, [mobileView, isDesktop]);

  return (
    <main className="TrainingCompletionPage">
      {isDesktop && (
        <div className="TrainingCompletionPage__header">
          <Logo />
        </div>
      )}
      <div
        className={`TrainingCompletionPage__body${
          isChatUnavailable || isChatPending
            ? " TrainingCompletionPage__body--noChat"
            : ""
        }`}
      >
        {showCelebration && (
          <div className="TrainingCompletionPage__content">
            <img
              src={ChecklistIllustration}
              alt=""
              role="presentation"
              className="TrainingCompletionPage__illustration"
            />
            <Typography
              desktop="h3"
              tagVariant="h2"
              color="stoneBlue"
              weight="regular"
              className="TrainingCompletionPage__subtitle"
            >
              {t("subtitle")}
            </Typography>
            {recordingLoading ? (
              <Skeleton variant="text" width={300} height={100} />
            ) : (
              <Typography
                desktop="h1"
                mobile="h1"
                tagVariant="h1"
                color="stoneBlue"
                className="TrainingCompletionPage__title"
              >
                {t("completedTitle", {
                  title: touchpointTitle || t("titleFallback"),
                })}
              </Typography>
            )}
            <Typography
              desktop="h3"
              color="secondary"
              className="TrainingCompletionPage__message"
            >
              {t("message")}
            </Typography>
            {!isDesktop && !isChatUnavailable && !isChatPending && (
              <Button
                ref={openChatButtonRef}
                variant="contained"
                color="primary"
                onClick={() => setMobileView("chat")}
                aria-expanded={mobileView === "chat"}
                aria-controls={CHAT_REGION_ID}
                className="TrainingCompletionPage__openChat"
              >
                <span
                  aria-hidden="true"
                  className="TrainingCompletionPage__openChat__icon"
                >
                  <Icon type={IconTypes.ChatPerson} color="white" size={20} />
                </span>
                {t("aiCoach.openChat")}
              </Button>
            )}
          </div>
        )}
        {showChat && (
          <div
            id={CHAT_REGION_ID}
            ref={chatRegionRef}
            tabIndex={-1}
            className="TrainingCompletionPage__chat"
          >
            <TrainingChatBot
              trainingModuleTitle={trainingModuleTitle}
              messages={chatMessages}
              isReplying={sendMutation.isLoading}
              disabled={isQuotaExhausted || chatInitFailed || chatInitLoading}
              onSend={handleSend}
              headerSlot={
                isDesktop ? undefined : <Logo width={56} height={24} />
              }
              onClose={
                isDesktop ? undefined : () => setMobileView("celebration")
              }
            />
          </div>
        )}
      </div>
    </main>
  );
};
